/**
 * The dashboard's charts, drawn as SVG.
 *
 * One axis, recessive dashed gridlines, thin marks, a 2px surface gap between
 * stacked segments, and a hover layer on every chart: a band over the month
 * and a tooltip naming each series with its value (docs/video-notes/07 —
 * "Dec 25 • 8 New • 2 Completed • 58 Ongoing"). Colour follows the series,
 * never its rank, so picking one series out does not repaint it.
 */
import { useEffect, useLayoutEffect, useRef, useState, type FC, type ReactNode, type RefObject } from 'react'
import { formatINR } from '@project/contracts'
import './dashboard.css'

export type FigureFormat = 'money' | 'qty' | 'count' | 'pct' | undefined

/**
 * Series colours, in a fixed order. Validated as a categorical set (lightness
 * band, chroma floor, colour-blind separation of neighbours); the amber is
 * under 3:1 against white, which is why every series is also named with its
 * value in the legend and the tooltip.
 */
export const SERIES_COLOURS = [
  'var(--chart-1)', 'var(--chart-4)', 'var(--chart-3)', 'var(--chart-5)', 'var(--chart-7)', 'var(--chart-6)',
] as const

export const colourAt = (i: number): string => SERIES_COLOURS[Math.min(i, SERIES_COLOURS.length - 1)]!

/** A figure in full, the way the widget's format writes it. */
export const formatFigure = (value: number | string, format: FigureFormat): string => {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  switch (format) {
    case 'money':
    case undefined:
      return formatINR(n.toFixed(2))
    case 'pct':
      return `${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`
    case 'qty':
      return n.toLocaleString('en-IN', { maximumFractionDigits: 3 })
    default:
      return n.toLocaleString('en-IN', { maximumFractionDigits: 1 })
  }
}

/** The same, short enough for an axis: crore, lakh, thousand. */
export const formatAxis = (n: number, format: FigureFormat): string => {
  if (format === 'pct') return `${Math.round(n)}%`
  const a = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  const sym = format === 'money' || format === undefined ? '₹' : ''
  const trim = (v: number): string => (Math.round(v * 10) / 10).toLocaleString('en-IN')
  if (sym && a >= 1e7) return `${sign}${sym}${trim(a / 1e7)} Cr`
  if (sym && a >= 1e5) return `${sign}${sym}${trim(a / 1e5)} L`
  if (a >= 1e3) return `${sign}${sym}${trim(a / 1e3)} K`
  return `${sign}${sym}${trim(a)}`
}

/** Round tick steps that cover [min, max], zero always included. */
export const niceTicks = (min: number, max: number, count = 4): number[] => {
  const lo = Math.min(0, min)
  const hi = Math.max(0, max)
  if (hi === lo) return [0, 1]
  const raw = (hi - lo) / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * mag >= raw) ?? 10) * mag
  const out: number[] = []
  for (let v = Math.floor(lo / step) * step; v <= hi + step * 0.001; v += step) out.push(Math.round(v / step) * step)
  if (out[out.length - 1]! < hi) out.push(out[out.length - 1]! + step)
  return out
}

/** The container's width, followed as it changes. */
const useWidth = (fallback: number): [RefObject<HTMLDivElement>, number] => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(fallback)
  useLayoutEffect(() => {
    const el = ref.current
    if (el && el.clientWidth > 0) setWidth(el.clientWidth)
  }, [])
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => { if (el.clientWidth > 0) setWidth(el.clientWidth) })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width]
}

export interface ChartSeries {
  readonly name: string
  readonly points: readonly string[]
  /** Its colour, fixed by its place among every series the widget has. */
  readonly colour: string
}

export type ChartType = 'bar' | 'stacked' | 'area' | 'line'

const M = { top: 12, right: 12, bottom: 30, left: 72 }
const HEIGHT = 260

export const SeriesChart: FC<{
  labels: readonly string[]
  series: readonly ChartSeries[]
  chart: ChartType
  format: FigureFormat
}> = ({ labels, series, chart, format }) => {
  const [ref, width] = useWidth(720)
  const [hover, setHover] = useState<number | null>(null)
  const n = labels.length
  const plotW = Math.max(120, width - M.left - M.right)
  const plotH = HEIGHT - M.top - M.bottom
  const value = (s: ChartSeries, x: number): number | null => {
    const v = s.points[x]
    return v === undefined || v === '' ? null : Number(v)
  }

  // The extent: stacked columns add up per month, everything else is per point.
  let lo = 0
  let hi = 0
  for (let x = 0; x < n; x += 1) {
    if (chart === 'stacked') {
      let up = 0
      let down = 0
      for (const s of series) { const v = value(s, x) ?? 0; if (v >= 0) up += v; else down += v }
      hi = Math.max(hi, up)
      lo = Math.min(lo, down)
    } else {
      for (const s of series) { const v = value(s, x); if (v !== null) { hi = Math.max(hi, v); lo = Math.min(lo, v) } }
    }
  }
  const ticks = niceTicks(lo, hi)
  const yMin = ticks[0]!
  const yMax = ticks[ticks.length - 1]!
  const y = (v: number): number => M.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH
  const band = plotW / Math.max(n, 1)
  const cx = (x: number): number => M.left + band * x + band / 2
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 56))))

  const marks: ReactNode[] = []
  if (chart === 'bar') {
    const groupW = Math.min(band * 0.72, 22 * series.length + 2 * (series.length - 1))
    const barW = Math.max(2, (groupW - 2 * (series.length - 1)) / Math.max(series.length, 1))
    series.forEach((s, i) => {
      for (let x = 0; x < n; x += 1) {
        const v = value(s, x)
        if (v === null || v === 0) continue
        const left = cx(x) - groupW / 2 + i * (barW + 2)
        marks.push(<rect key={`${s.name}-${x}`} x={left} width={barW} y={Math.min(y(v), y(0))}
          height={Math.max(1, Math.abs(y(v) - y(0)))} rx={Math.min(4, barW / 2)} fill={s.colour} />)
      }
    })
  } else if (chart === 'stacked') {
    const barW = Math.min(band * 0.6, 40)
    for (let x = 0; x < n; x += 1) {
      let up = 0
      let down = 0
      series.forEach((s) => {
        const v = value(s, x) ?? 0
        if (v === 0) return
        const from = v >= 0 ? up : down
        const to = from + v
        if (v >= 0) up = to; else down = to
        marks.push(<rect key={`${s.name}-${x}`} x={cx(x) - barW / 2} width={barW} y={Math.min(y(from), y(to))}
          height={Math.max(1, Math.abs(y(to) - y(from)))} rx={3} fill={s.colour}
          stroke="var(--surface)" strokeWidth={2} />)
      })
    }
  } else {
    series.forEach((s) => {
      // A gap in the data is a gap in the line: the S-curve's actual stops today.
      const runs: Array<Array<[number, number]>> = []
      let run: Array<[number, number]> = []
      for (let x = 0; x < n; x += 1) {
        const v = value(s, x)
        if (v === null) { if (run.length) runs.push(run); run = []; continue }
        run.push([cx(x), y(v)])
      }
      if (run.length) runs.push(run)
      runs.forEach((r, k) => {
        const d = r.map(([px, py], j) => `${j === 0 ? 'M' : 'L'}${px.toFixed(1)},${py.toFixed(1)}`).join(' ')
        if (chart === 'area') {
          const area = `${d} L${r[r.length - 1]![0].toFixed(1)},${y(0).toFixed(1)} L${r[0]![0].toFixed(1)},${y(0).toFixed(1)} Z`
          marks.push(<path key={`${s.name}-a${k}`} d={area} fill={s.colour} fillOpacity={0.16} />)
        }
        marks.push(<path key={`${s.name}-l${k}`} d={d} fill="none" stroke={s.colour} strokeWidth={2} strokeLinejoin="round" />)
        if (r.length === 1 || chart === 'line') {
          r.forEach(([px, py], j) => marks.push(
            <circle key={`${s.name}-p${k}-${j}`} cx={px} cy={py} r={3} fill={s.colour} stroke="var(--surface)" strokeWidth={1.5} />))
        }
      })
    })
  }

  const tip = hover === null ? null : (
    <div className="chart-tip" role="status"
      style={{ left: Math.min(Math.max(cx(hover) + 12, 0), Math.max(0, width - 220)), top: M.top }}>
      <div className="chart-tip__head">{labels[hover]}</div>
      {series.map((s) => {
        const v = value(s, hover)
        return (
          <div className="chart-tip__row" key={s.name}>
            <span className="legend__swatch" style={{ background: s.colour }} />
            <span className="chart-tip__val num">{v === null ? '—' : formatFigure(v, format)}</span>
            <span className="chart-tip__name">{s.name}</span>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="chart-svg" ref={ref} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={HEIGHT} role="img"
        aria-label={`${series.map((s) => s.name).join(', ')} by month`}>
        {hover !== null ? (
          <rect x={M.left + band * hover} y={M.top} width={band} height={plotH} fill="var(--hover)" />
        ) : null}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={M.left + plotW} y1={y(t)} y2={y(t)}
              stroke={t === 0 ? 'var(--line-strong)' : 'var(--line)'} strokeDasharray={t === 0 ? undefined : '4 4'} />
            <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="chart-svg__tick">{formatAxis(t, format)}</text>
          </g>
        ))}
        {marks}
        {hover !== null && (chart === 'area' || chart === 'line') ? (
          <line x1={cx(hover)} x2={cx(hover)} y1={M.top} y2={M.top + plotH} stroke="var(--line-strong)" />
        ) : null}
        {labels.map((l, x) => (x % every === 0 ? (
          <text key={l + x} x={cx(x)} y={HEIGHT - 10} textAnchor="middle" className="chart-svg__tick">{l}</text>
        ) : null))}
        {labels.map((l, x) => (
          <rect key={`hit-${l}-${x}`} x={M.left + band * x} y={M.top} width={band} height={plotH + M.bottom - 4}
            fill="transparent" onMouseEnter={() => setHover(x)} onFocus={() => setHover(x)} tabIndex={-1}>
            <title>{`${l} • ${series.map((s) => `${formatFigure(value(s, x) ?? 0, format)} ${s.name}`).join(' • ')}`}</title>
          </rect>
        ))}
      </svg>
      {tip}
    </div>
  )
}

/** One figure as a ring: "90% of estimated cost". */
export const Ring: FC<{ pct: number; label: string; note: string; colour?: string }> = ({ pct, label, note, colour = 'var(--chart-1)' }) => {
  const r = 42
  const c = 2 * Math.PI * r
  const shown = Math.max(0, Math.min(100, pct))
  return (
    <div className="ring">
      <svg width={110} height={110} viewBox="0 0 110 110" role="img" aria-label={`${label}: ${pct.toFixed(0)}% ${note}`}>
        <circle cx={55} cy={55} r={r} fill="none" stroke="var(--track-bg)" strokeWidth={10} />
        {shown > 0 ? (
          <circle cx={55} cy={55} r={r} fill="none" stroke={colour} strokeWidth={10} strokeLinecap="round"
            strokeDasharray={`${(shown / 100) * c} ${c}`} transform="rotate(-90 55 55)" />
        ) : null}
        <text x={55} y={55} dy="0.35em" textAnchor="middle" className="ring__pct">{`${Math.round(pct)}%`}</text>
      </svg>
      <div className="ring__label">{label}</div>
      <div className="ring__note">{note}</div>
    </div>
  )
}

/** Parts of a whole as a ring, the total in the middle. */
export const SplitRing: FC<{ parts: ReadonlyArray<{ label: string; value: number }>; total: string; totalLabel: string }> = ({ parts, total, totalLabel }) => {
  const r = 46
  const c = 2 * Math.PI * r
  const sum = parts.reduce((a, p) => a + Math.max(0, p.value), 0)
  let offset = 0
  return (
    <svg width={130} height={130} viewBox="0 0 130 130" role="img" aria-label={`${total} ${totalLabel}`}>
      <circle cx={65} cy={65} r={r} fill="none" stroke="var(--track-bg)" strokeWidth={12} />
      {sum > 0 ? parts.map((p, i) => {
        const len = (Math.max(0, p.value) / sum) * c
        const el = (
          <circle key={p.label} cx={65} cy={65} r={r} fill="none" stroke={colourAt(i)} strokeWidth={12}
            strokeDasharray={`${Math.max(0, len - 2)} ${c}`} strokeDashoffset={-offset} transform="rotate(-90 65 65)" />
        )
        offset += len
        return el
      }) : null}
      <text x={65} y={60} textAnchor="middle" className="ring__pct">{total}</text>
      <text x={65} y={80} textAnchor="middle" className="chart-svg__tick">{totalLabel}</text>
    </svg>
  )
}

/**
 * A ratio against the 0 – 0.9 – 1 – 2 band: under 0.9 is poor, 0.9 to 1 is
 * to watch, over 1 is good (docs/03 §3).
 */
export const RatioBand: FC<{ ratio: number | null }> = ({ ratio }) => {
  const at = ratio === null ? null : (Math.max(0, Math.min(2, ratio)) / 2) * 100
  return (
    <div className="ratio" role="img"
      aria-label={ratio === null ? 'No ratio yet' : `Ratio ${ratio.toFixed(2)}: ${ratio < 0.9 ? 'below 0.9' : ratio < 1 ? 'between 0.9 and 1' : 'above 1'}`}>
      <div className="ratio__track">
        <span className="ratio__band ratio__band--bad" style={{ width: '45%' }} />
        <span className="ratio__band ratio__band--warn" style={{ width: '5%' }} />
        <span className="ratio__band ratio__band--good" style={{ width: '50%' }} />
        {at === null ? null : <span className="ratio__marker" style={{ left: `${at}%` }} title={ratio!.toFixed(2)} />}
      </div>
      <div className="ratio__scale">
        <span style={{ left: '0%' }}>0</span>
        <span style={{ left: '45%' }}>0.9</span>
        <span style={{ left: '50%' }}>1</span>
        <span style={{ left: '100%' }}>2</span>
      </div>
      {ratio === null ? null : <div className="ratio__value num">{ratio.toFixed(2)}</div>}
    </div>
  )
}
